import { RocketLaunchIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/form";
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  StatCard,
  type Tone,
} from "../components/ui/misc";
import { api, errorMessage } from "../lib/api";
import { formatDateTime, formatNumber, relativeTime } from "../lib/format";
import { catalogReleasesQuery, catalogStatusQuery } from "../lib/queries";
import { useCan } from "../lib/session";

const tones: Record<string, Tone> = {
  requested: "info",
  triggered: "success",
  failed: "danger",
  skipped: "neutral",
};

export function PublishingPage() {
  const queryClient = useQueryClient();
  const canPublish = useCan("catalog.publish");
  const status = useQuery(catalogStatusQuery);
  const releases = useQuery(catalogReleasesQuery);
  const [note, setNote] = useState("");

  const publish = useMutation({
    mutationFn: () => api<unknown>("/admin/catalog/publish", { body: { note: note || null } }),
    onSuccess: async () => {
      setNote("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["catalog-status"] }),
        queryClient.invalidateQueries({ queryKey: ["catalog-releases"] }),
        queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      ]);
      toast.success("Release requested; the site rebuild runs in the background");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (status.error)
    return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  if (status.isPending) return <Loading />;

  return (
    <>
      <PageHeader
        title="Publishing"
        description="The public site is a static build; publishing snapshots the catalog and triggers a deploy."
      />

      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Live games" value={formatNumber(status.data.publishedGameCount)} />
        <StatCard
          label="Waiting to publish"
          value={formatNumber(status.data.changedSinceLastRelease)}
          tone={status.data.changedSinceLastRelease > 0 ? "warning" : undefined}
        />
        <StatCard
          label="Last release"
          value={
            status.data.lastRelease ? relativeTime(status.data.lastRelease.createdAt) : "Never"
          }
          hint={status.data.lastRelease?.status ?? "Publish to create the first release"}
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,360px)_1fr]">
        <Card title="Publish catalog">
          {!status.data.deployHookConfigured && (
            <p className="border-gold-line bg-gold-bg text-gold-text text-caption mb-3 rounded border px-3 py-2">
              No deploy hook is set up yet, so a release is recorded but the site will not rebuild.
              Add CATALOG_DEPLOY_HOOK_URL to the API worker.
            </p>
          )}
          <div className="flex flex-col gap-3">
            <Field label="Release note" hint="Optional; shown in the release history.">
              <Input
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Autumn batch"
              />
            </Field>
            <Button
              variant="primary"
              disabled={!canPublish || publish.isPending}
              onClick={() => publish.mutate()}
            >
              <RocketLaunchIcon /> {publish.isPending ? "Requesting…" : "Publish now"}
            </Button>
            {!canPublish && (
              <p className="text-muted text-caption">Your role cannot trigger releases.</p>
            )}
          </div>
        </Card>

        <Card title="Release history">
          {releases.isPending ? (
            <Loading />
          ) : releases.data && releases.data.items.length > 0 ? (
            <table className="text-ui w-full border-collapse text-left">
              <thead className="bg-well">
                <tr className="border-line border-b">
                  <th className="text-micro text-faint h-9 pr-3 font-semibold uppercase">When</th>
                  <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Status</th>
                  <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Games</th>
                  <th className="text-micro text-faint h-9 px-3 font-semibold uppercase">Note</th>
                </tr>
              </thead>
              <tbody>
                {releases.data.items.map((release) => (
                  <tr key={release.id} className="t-row border-line/60 border-b last:border-0">
                    <td className="text-muted text-caption h-10 pr-3">
                      {formatDateTime(release.createdAt)}
                    </td>
                    <td className="px-3">
                      <Badge tone={tones[release.status] ?? "neutral"}>{release.status}</Badge>
                    </td>
                    <td className="tabular px-3">{formatNumber(release.publishedGameCount)}</td>
                    <td className="text-body text-caption px-3">
                      {release.error ?? release.note ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <EmptyState title="Nothing published yet">
              Your first release will show up here, with who ran it and how many games went live.
            </EmptyState>
          )}
        </Card>
      </div>
    </>
  );
}
